import { InMemoryCache } from '@apollo/client'

import { newVersionBanner } from '@/components'
import { clientFeatureTogglesCache } from '@/components/feature-toggle/feature-toggle'
import { appState } from '@/modules/app/app.state'
import { searchState } from '@/modules/search'
import { relayStylePagination } from '@/utils/relayStylePagination'

const paginate = (keys: string[] = []) =>
  relayStylePagination((o) => {
    if (!o) return []
    const obj = {}
    if ('last' in o.pagination) {
      obj['last'] = o.pagination.last
    } else if ('first' in o.pagination) {
      obj['first'] = o.pagination.first
    }
    keys.forEach((k) => (obj[k] = o[k]))
    return JSON.stringify(obj)
  })

export const cache = new InMemoryCache({
  typePolicies: {
    Query: {
      fields: {
        appState() {
          return appState()
        },
        searchState() {
          return searchState()
        },
        newVersionBanner() {
          return newVersionBanner()
        },
        clientFeatureTogglesCache() {
          return clientFeatureTogglesCache()
        },
        viewer: {
          keyArgs: false,
        },
        groups: paginate(),
        users: paginate(),
        knowledgeGroups: paginate(),
        announcementPosts: paginate(),
      },
    },
    FeatureToggle: {
      keyFields: ['feature'],
    },
    User: {
      keyFields: ['userId'],
      fields: {
        createdPosts: paginate(['isArchived']),
        followingPosts: paginate(),
        commentedPosts: paginate(),
        likedPosts: paginate(),
        favoredPosts: paginate(),
        createdGroups: paginate(['isArchived']),
        followingGroups: paginate(['filter']),
        followingUsers: paginate(),
        followedUsers: paginate(),
        groups: paginate(),
        notifications: paginate(),
        thanksPosts: paginate(),
        allPosts: paginate(),
        yesterdayVisitors: paginate(),
        unStartEvents: paginate(),
        finishedEvents: paginate(),
        timelineEvents: paginate(),
        viewedUsers: paginate(),
        skills: paginate(),
        hobbies: paginate(),
      },
    },
    Post: {
      keyFields: ['postId'],
      fields: {
        likedUsers: paginate(),
        readUsers: paginate(),
        feltUsefulUsers: paginate(),
        comments: relayStylePagination(),
      },
    },
    Section: {
      keyFields: ['sectionId'],
      fields: {
        posts: paginate(),
      },
    },
    Group: {
      keyFields: ['groupId'],
      fields: {
        followers: paginate(),
        posts: paginate(),
        files: paginate(),
        admins: paginate(),
        unreadMemberRequests: paginate(),
      },
    },
    KnowledgeNS: {
      keyFields: [],
      fields: {
        posts: paginate(),
        groups: paginate(),
      },
    },
    KnowledgePost: {
      keyFields: ['knowledgePostId'],
    },
    KnowledgeGroup: {
      keyFields: ['knowledgeGroupId'],
      fields: {
        posts: paginate(),
      },
    },
    Comment: {
      keyFields: ['commentId'],
      fields: {
        comments: relayStylePagination(),
      },
    },
    File: {
      keyFields: ['fileId'],
    },
    Department: {
      keyFields: ['departmentId'],
      fields: {
        journals: paginate(),
      },
    },
    Recommend: {
      keyFields: [],
      fields: {
        trendingPosts: paginate(['timeSpan']),
        featuredPosts: paginate(),
        banners: paginate(),
      },
    },
    WebsiteNS: {
      keyFields: [],
      fields: {
        categories: paginate(),
        entities: paginate(),
      },
    },
    WebsiteCategory: {
      keyFields: ['websiteCategoryId'],
      fields: {
        websites: paginate(),
      },
    },
    Website: {
      keyFields: ['websiteId'],
    },
    GroupCategory: {
      keyFields: ['groupCategoryId'],
    },
    NotificationSetting: {
      keyFields: ['notificationSettingId'],
    },
    EventParticipant: {
      keyFields: ['eventParticipantId'],
    },
    Event: {
      keyFields: ['eventId'],
      fields: {
        participants: paginate(),
        interestedUsers: paginate(),
      },
    },
    Announcement: {
      keyFields: ['announcementId'],
    },
    AnniversaryCard: {
      keyFields: ['anniversaryCardId'],
    },
    AnniversaryCardSet: {
      keyFields: ['anniversary'],
      fields: {
        unFinishedUsers: paginate(),
        finishedUsers: paginate(),
        blockedUsers: paginate(),
      },
    },
    Skill: {
      keyFields: ['skillId'],
      fields: {
        evaluatedUsers: paginate(),
      },
    },
    Hobby: {
      keyFields: ['hobbyId'],
      fields: {
        evaluatedUsers: paginate(),
      },
    },
    // ChatGptGroup: {
    //   keyFields: ['chatgptGroupId'],
    //   fields: {
    //     messages: paginate(),
    //   },
    // },
  },
})
